import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ACCOUNT_COLORS, ACCOUNT_LABEL } from "@/lib/api";
import { toast } from "sonner";
import ChallengeBanner from "@/components/ChallengeBanner";
import WeeklyStrip, { buildWeekDays, weekRangeLabel } from "@/components/WeeklyStrip";
import RedemptionTask from "@/components/RedemptionTask";
import BrandLogo from "@/components/BrandLogo";
import CoronationBanner from "@/components/CoronationBanner";
import BottomNav from "@/components/BottomNav";
import Leaderboard from "@/components/Leaderboard";
import LeagueFeed from "@/components/LeagueFeed";
import ActivityLog from "@/components/ActivityLog";
import PastWinners from "@/components/PastWinners";
import BragBoardPanel from "@/components/BragBoardPanel";
import WeeklyHeatmap from "@/components/WeeklyHeatmap";
import RogueDirectiveCard from "@/components/RogueDirectiveCard";
import { stakesForDate } from "@/lib/stakes";
import { clearDeviceUserId } from "@/lib/league";
import { isPushSupported, enablePushForUser } from "@/lib/push";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

// V2 — 60-second auto-logout intentionally removed so the localStorage
// session persists indefinitely on the user's personal device.

export default function PersonalDashboard() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [league, setLeague] = useState(null);
  const [challenge, setChallenge] = useState(null);
  // Phase 19 — mobile-only bottom-nav tab state (desktop renders the
  // full multi-column dashboard unchanged). Default tab is "home" so the
  // first surface post-login is always the daily check-ins, matching spec.
  const [mobileTab, setMobileTab] = useState("home");

  const [today] = useState(todayISO());
  const [selectedDate, setSelectedDate] = useState(todayISO());
  // V2 — viewed-week offset (0 = this week, -1 = last week, …). Pagination
  // arrows on the strip mutate this. The selectedDate stays absolute so the
  // backend's grace-cutoff yesterday-rule keeps working across rollovers.
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayState, setDayState] = useState(null);
  const [rules, setRules] = useState(["","","","",""]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputs = useRef({});

  const loadDay = useCallback(async (uid, date) => {
    const d = await api.getUserDay(uid, date);
    setDayState(d);
    if (date === todayISO()) {
      setRules(d.items.map((it) => it.rule_text));
    }
  }, []);

  useEffect(() => {
    // Reset stale state on every userId switch so the header can never
    // bleed cached strings from a previous profile/league context.
    setUser(null);
    setTasks(null);
    setLeague(null);
    setChallenge(null);
    setDayState(null);
    setLoading(true);
    (async () => {
      try {
        // 1) Source-of-truth: load the user from the URL first; their
        //    `league_id` is the only valid league for THIS dashboard.
        const u = await api.getUser(userId);
        // 2) Parallel: tasks + league-by-user's-league-id + challenge.
        //    Critically, getLeagueById(u.league_id) replaces the legacy
        //    api.getLeague() global-fallback that was bleeding the
        //    most-recent league name/code into every dashboard header.
        const [t, l, ch] = await Promise.all([
          api.getTasks(userId),
          api.getLeagueById(u.league_id),
          api.getChallengeInfo(),
        ]);
        setUser(u);
        setTasks(t);
        setLeague(l);
        setChallenge(ch);
        setRules((t.rules || []).concat(["","","","",""]).slice(0, 5));
        await loadDay(userId, todayISO());
      } catch {
        toast.error("Couldn't load profile");
        nav("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, nav, loadDay]);

  // Reload day data when switching dates
  useEffect(() => {
    if (!user) return;
    loadDay(user.id, selectedDate);
  }, [selectedDate, user, loadDay]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-mono">LOADING…</div>
      </div>
    );
  }
  if (!user || !challenge) return null;

  const isToday = selectedDate === today;
  const inFuture = dayState?.is_future;
  const editable = !!dayState?.is_editable;
  const rulesLocked = !!tasks?.locked || league?.status === "active";
  const color = ACCOUNT_COLORS[user.account_type];

  // Date-context stakes: reactive to selectedDate (not just today)
  const sel = stakesForDate(selectedDate, challenge);
  const stakesYes = sel.yes;
  const stakesNo = sel.no;
  const isChallenge = sel.isChallenge;
  const isWeekend = sel.isWeekend;

  const isAdmin = user.account_type === "admin";

  const logout = () => {
    clearDeviceUserId();
    toast.success("Logged out of this device");
    nav("/");
  };

  const setRule = (i, v) => {
    const next = [...rules]; next[i] = v; setRules(next);
  };

  const saveRules = async () => {
    try {
      const t = await api.updateTasks(user.id, rules);
      setTasks(t);
      toast.success("Rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const refreshUser = async () => {
    const u = await api.getUser(user.id);
    setUser(u);
  };

  const toggleCheckin = async (i, nextValue) => {
    if (!editable) {
      toast.error("Day is locked — view only");
      return;
    }
    const item = dayState?.items?.[i];
    if (!item?.rule_text || !item.rule_text.trim()) {
      toast.error("Set a rule before checking in");
      return;
    }

    // ---------- Phase 20.6 — Optimistic UI ----------
    // Snapshot for rollback. We capture the FULL dayState + user.points_total
    // so we can roll back atomically if the server rejects the change.
    const prevDayState = dayState;
    const prevUser = user;
    const currentValue = item.value;
    const clearing = currentValue === nextValue;            // clicking the active button clears the row
    const oldDelta = item.points || 0;
    const newDelta = clearing
      ? 0
      : (nextValue === "yes" ? (item.points_yes ?? 10) : (item.points_no ?? -10));
    const totalDeltaShift = newDelta - oldDelta;

    // Build the optimistic next item + dayState
    const nextItem = {
      ...item,
      value: clearing ? null : nextValue,
      points: newDelta,
      // photo_path stays — toggling shouldn't drop an existing proof
    };
    const nextItems = dayState.items.map((it, idx) => (idx === i ? nextItem : it));
    setDayState({
      ...dayState,
      items: nextItems,
      day_total: (dayState.day_total || 0) + totalDeltaShift,
    });
    setUser({ ...user, points_total: (user.points_total || 0) + totalDeltaShift });

    try {
      if (clearing) {
        await api.clearCheckin(user.id, selectedDate, i);
      } else {
        await api.upsertCheckin(user.id, selectedDate, i, nextValue);
      }
      // Background reconcile — picks up server-side side effects we can't
      // predict locally (consistency bonus, challenge-day risk-free overrides,
      // streak_active recompute, etc.). UI is already responsive; this just
      // ensures eventual consistency.
      loadDay(user.id, selectedDate);
      refreshUser();
    } catch (e) {
      // Rollback to the pre-click snapshot, then surface a tactical toast.
      setDayState(prevDayState);
      setUser(prevUser);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `[ REVERTED: ${String(detail).toUpperCase()} ]` : "[ REVERTED: NETWORK ERROR ]");
    }
  };

  const onPickPhoto = (i) => fileInputs.current[i]?.click();

  const handleFile = async (i, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo too large (8MB max)");
      return;
    }
    setUploadingIdx(i);
    try {
      await api.uploadCheckinPhoto(user.id, selectedDate, i, file);
      toast.success("Posted to the league feed");
      await refreshUser();
      await loadDay(user.id, selectedDate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const negative = user.points_total < 0;
  const items = dayState?.items || [];

  let stakesLabel;
  if (isChallenge) stakesLabel = "CHALLENGE DAY";
  else if (isWeekend) stakesLabel = "WEEKEND DOUBLE";
  else stakesLabel = "WEEKDAY";
  if (!isToday && !editable) stakesLabel += " · VIEW-ONLY";
  else if (!isToday && editable) stakesLabel += " · GRACE";

  const days = buildWeekDays(today, weekOffset);
  const weekLabel = weekRangeLabel(days, today, weekOffset);
  const canGoForward = weekOffset < 0;

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden"
      style={league?.status === "active" ? { boxShadow: "inset 0 0 240px rgba(255,59,48,0.1)" } : {}}
      data-testid="dashboard-shell"
    >
      {/* Phase 4.2 — Coronation banner (edge-to-edge, above sticky header) */}
      <div className="flex-shrink-0">
        <CoronationBanner leagueId={league?.id} />
      </div>

      {/* Top bar — non-shrinking flex child (replaces window-scroll sticky) */}
      <header className="flex-shrink-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-white/10 safe-top">
        <div className="max-w-[1600px] mx-auto px-4 md:px-12 py-3 md:py-4 flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 order-1">
            <BrandLogo size="sm" showWordmark={false} className="sm:hidden flex-shrink-0" />
            <BrandLogo size="sm" className="hidden sm:flex flex-shrink-0" />
            {league?.code && (
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(league.code);
                    toast.success(`Code ${league.code} copied`);
                  } catch { /* ignore */ }
                }}
                className="label-mono px-2 sm:px-3 h-9 border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] hover-flip flex-shrink-0 whitespace-nowrap"
                data-testid="dashboard-code-button"
                title="Tap to copy league code"
              >
                #{league.code}
              </button>
            )}
            <div className="font-anton text-base sm:text-xl uppercase truncate min-w-0" data-testid="dashboard-league-title">
              {league?.name}
            </div>
          </div>
          <PushBell userId={user.id} />
          <button
            onClick={logout}
            className="order-2 sm:order-3 label-mono px-2 sm:px-3 h-9 border border-[var(--stakes-border)] hover:border-[var(--stakes-primary)] hover:text-[var(--stakes-primary)] hover-flip flex-shrink-0"
            data-testid="dashboard-logout-button"
            title="Log out of this device"
          >
            ⎋
          </button>
          <span
            key={stakesLabel}
            className="label-mono px-2 sm:px-3 py-1 border whitespace-nowrap order-3 sm:order-2 w-full sm:w-auto text-center sm:text-left overflow-hidden text-ellipsis haptic-flip"
            style={{
              borderColor: isChallenge ? "var(--stakes-admin)" : isWeekend ? "var(--stakes-primary)" : "var(--stakes-border)",
              color: isChallenge ? "var(--stakes-admin)" : isWeekend ? "var(--stakes-primary)" : "var(--stakes-muted)",
            }}
            data-testid="dashboard-stakes-badge"
          >
            ● +{stakesYes}/{stakesNo === 0 ? "0" : stakesNo} • {stakesLabel}
          </span>
        </div>
      </header>

      {/* === Scrollable viewport — flex:1 + overflow-y:auto is THE fix. The
          BottomNav lives outside this div (position:fixed on mobile) and we
          pad the bottom via mobile-bottom-pad so content clears the nav. === */}
      <div
        className="flex-1 overflow-y-auto mobile-bottom-pad"
        data-testid="dashboard-scroll-area"
      >
      {/* Desktop (≥1024px) 2-column grid: LEFT = Personal Ops (home content),
          RIGHT = Global Intel (Leaderboard + Proof Wall + Ledger).
          Below lg the grid collapses to a single column and we fall back to
          the mobileTab swap (rendered in the sibling sections further down). */}
      <div className="lg:max-w-[1600px] lg:mx-auto lg:px-12 lg:py-8 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start">
      <main
        className={`max-w-[1600px] mx-auto px-4 md:px-12 py-4 md:py-8 space-y-5 md:space-y-8 lg:max-w-none lg:mx-0 lg:px-0 lg:py-0 lg:min-w-0 ${mobileTab === "home" ? "" : "hidden lg:block"}`}
        data-testid="dashboard-home-col"
      >
        {isChallenge && isToday && <ChallengeBanner info={challenge} compact />}

        {/* Player identity card (compact) */}
        <section className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)] p-4 md:p-6 flex items-end gap-3 sm:gap-4 md:gap-6 flex-wrap">
          <div className="flex-1 min-w-0 basis-full sm:basis-auto">
            <div className="label-mono flex items-center gap-2 flex-wrap" style={{ color }}>
              <span>{ACCOUNT_LABEL[user.account_type]}</span>
              {/* Phase 20.2 — stealth streak dot. Binary lit/dark, NO counter
                  exposed. Color shifts to the cyan child accent + pulses when
                  active; goes muted-gray + label "[ ○ IDLE ]" when dormant. */}
              <span
                className="label-mono inline-flex items-center gap-1"
                style={{
                  color: dayState?.streak_active ? "var(--stakes-child)" : "var(--stakes-muted)",
                  borderLeft: "1px solid var(--stakes-border)",
                  paddingLeft: 8,
                  marginLeft: 2,
                }}
                title={dayState?.streak_active ? "Consistency holding" : "Consistency broken"}
                data-testid="streak-indicator"
                data-streak-active={dayState?.streak_active ? "true" : "false"}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    background: dayState?.streak_active ? "var(--stakes-child)" : "var(--stakes-muted)",
                    boxShadow: dayState?.streak_active ? "0 0 8px var(--stakes-child)" : "none",
                    animation: dayState?.streak_active ? "stakes-streak-pulse 1.6s ease-in-out infinite" : "none",
                  }}
                />
                {dayState?.streak_active ? "ON FIRE" : "IDLE"}
              </span>
            </div>
            <h1 className="font-anton text-3xl sm:text-4xl md:text-6xl uppercase leading-[0.9] mt-1 break-words" data-testid="dashboard-user-name">
              {user.name}
            </h1>
          </div>
          <div>
            <div className="label-mono">TOTAL</div>
            <div
              className="font-anton text-4xl sm:text-5xl md:text-7xl leading-none"
              style={{ color: negative ? "var(--stakes-primary)" : "#fff" }}
              data-testid="dashboard-points-total"
            >
              {user.points_total > 0 ? "+" : ""}{user.points_total}
            </div>
          </div>
          <div className="text-right">
            <div className="label-mono">{isToday ? "TODAY" : dayLabel(selectedDate).toUpperCase()}</div>
            <div
              className="font-anton text-2xl sm:text-3xl md:text-4xl leading-none"
              style={{ color: (dayState?.day_total ?? 0) < 0 ? "var(--stakes-primary)" : "#fff" }}
              data-testid="dashboard-today-delta"
            >
              {(dayState?.day_total ?? 0) > 0 ? "+" : ""}{dayState?.day_total ?? 0}
            </div>
          </div>
        </section>

        {/* Phase 20.5 — Rogue Directive card. Renders only for laggard,
            leader, or admin-dev-override. No-op for bystanders. */}
        <RogueDirectiveCard user={user} leagueId={league?.id} />

        {/* Weekly calendar strip */}
        <section data-testid="weekly-strip-section">
          <div className="flex items-center justify-between mb-2 gap-3">
            <button
              type="button"
              onClick={() => setWeekOffset((w) => w - 1)}
              className="h-8 w-10 border-2 border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip font-mono text-lg leading-none flex items-center justify-center"
              data-testid="weekly-strip-prev"
              title="Previous week"
              aria-label="Previous week"
            >
              ‹
            </button>
            <div className="label-mono flex-1 text-center" data-testid="weekly-strip-label">
              {weekLabel}
            </div>
            <button
              type="button"
              onClick={() => setWeekOffset((w) => Math.min(0, w + 1))}
              disabled={!canGoForward}
              className="h-8 w-10 border-2 border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip font-mono text-lg leading-none flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current"
              data-testid="weekly-strip-next"
              title={canGoForward ? "Next week" : "Already on current week"}
              aria-label="Next week"
            >
              ›
            </button>
          </div>
          <WeeklyStrip
            days={days}
            selected={selectedDate}
            onSelect={setSelectedDate}
            today={today}
            graceCutoff={dayState?.grace_cutoff || today}
          />
        </section>

        {/* Redemption (Fri/Sat or always-visible bounty hint) — READ ONLY for all roles */}
        <RedemptionTask
          userId={user.id}
          leagueId={league.id}
          onChange={refreshUser}
        />

        {/* Phase 20.16 — 7-Day Heat. Refetches whenever the day-check-in
            state shifts (via `refreshKey`) so the current cell reflects
            the fresh point total after a YES/NO tap. */}
        <WeeklyHeatmap userId={user.id} refreshKey={dayState?.day_total} />

        {/* Day check-in panel */}
        <section className="space-y-3">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <div className="label-mono">DAILY CHECK-IN</div>
              <h2 className="font-barlow font-black text-2xl md:text-3xl uppercase mt-1 wrap-words">
                {dayLabel(selectedDate)}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {inFuture && <span className="label-mono" style={{ color: "var(--stakes-muted)" }}>FUTURE</span>}
              {!editable && !inFuture && (
                <span className="label-mono" style={{ color: "var(--stakes-primary)" }}>LOCKED · VIEW ONLY</span>
              )}
              {!rulesLocked && isToday && (
                <button
                  onClick={saveRules}
                  className="h-10 px-3 label-mono border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
                  data-testid="dashboard-save-rules-button"
                >
                  SAVE RULE EDITS
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {items.map((it, i) => {
              const val = it.value;
              const photoPath = it.photo_path;
              const isAuto = it.is_auto;
              const yesLabel = `+${stakesYes}`;
              const noLabel = stakesNo === 0 ? "0" : `${stakesNo}`;
              const showButtons = editable && !!it.rule_text;

              return (
                <div
                  key={i}
                  className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]"
                  data-testid={`dashboard-rule-row-${i}`}
                >
                  <div className="flex items-stretch gap-0">
                    <div
                      className="w-12 sm:w-16 flex items-center justify-center font-anton text-2xl sm:text-4xl flex-shrink-0"
                      style={{ background: color, color: "#000" }}
                    >
                      {i + 1}
                    </div>
                    {isToday && !rulesLocked ? (
                      <input
                        value={rules[i] || ""}
                        onChange={(e) => setRule(i, e.target.value)}
                        onBlur={saveRules}
                        placeholder="Add a habit rule"
                        className="flex-1 min-w-0 bg-transparent border-0 outline-none px-3 sm:px-5 font-barlow text-base sm:text-lg md:text-xl uppercase tracking-wide wrap-words"
                        data-testid={`dashboard-rule-input-${i}`}
                      />
                    ) : (
                      <div className="flex-1 min-w-0 px-3 sm:px-5 py-2 font-barlow text-base sm:text-lg md:text-xl uppercase tracking-wide wrap-words flex items-center">
                        {it.rule_text || <span className="text-[var(--stakes-muted)]">— NOT SET —</span>}
                      </div>
                    )}
                    {showButtons && (
                      <div className="flex flex-shrink-0">
                        <button
                          disabled={busy}
                          onClick={() => toggleCheckin(i, "yes")}
                          className={`w-14 sm:w-20 font-barlow font-black uppercase tracking-wider border-l border-[var(--stakes-border)] hover-flip ${
                            val === "yes" ? "text-black" : "text-[var(--stakes-muted)] hover:bg-white/5"
                          }`}
                          style={val === "yes" ? { background: "var(--stakes-child)" } : {}}
                          data-testid={`checkin-yes-${i}`}
                        >
                          YES<br/><span className="font-mono-st text-[10px]">{yesLabel}</span>
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => toggleCheckin(i, "no")}
                          className={`w-14 sm:w-20 font-barlow font-black uppercase tracking-wider border-l border-[var(--stakes-border)] hover-flip ${
                            val === "no"
                              ? (stakesNo === 0 ? "text-black" : "text-white")
                              : "text-[var(--stakes-muted)] hover:bg-white/5"
                          }`}
                          style={val === "no" ? { background: stakesNo === 0 ? "var(--stakes-muted)" : "var(--stakes-primary)" } : {}}
                          data-testid={`checkin-no-${i}`}
                        >
                          NO<br/><span className="font-mono-st text-[10px]">{noLabel}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {val && (
                    <div
                      className="px-3 sm:px-5 py-2 flex items-center justify-between gap-3 flex-wrap"
                      style={{
                        background: val === "yes" ? "rgba(50,215,75,0.08)" : (it.points === 0 ? "rgba(255,214,10,0.08)" : "rgba(255,59,48,0.08)"),
                        color: val === "yes" ? "var(--stakes-child)" : (it.points === 0 ? "var(--stakes-admin)" : "var(--stakes-primary)"),
                      }}
                    >
                      <div className="label-mono">
                        {isAuto ? "AUTO-FAIL" : "LOGGED"} • {it.points > 0 ? `+${it.points}` : it.points} PTS
                        {editable && !isAuto && " • TAP AGAIN TO UNDO"}
                      </div>
                      {val === "yes" && editable && (
                        <div className="flex items-center gap-2">
                          {photoPath ? (
                            <a
                              href={api.fileUrl(photoPath)}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 label-mono hover:text-white"
                              data-testid={`checkin-photo-thumb-${i}`}
                            >
                              <img src={api.fileUrl(photoPath)} alt="proof" className="w-9 h-9 object-cover border border-[var(--stakes-border)]" />
                              POSTED
                            </a>
                          ) : (
                            <>
                              <input
                                ref={(el) => (fileInputs.current[i] = el)}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => handleFile(i, e.target.files?.[0])}
                                data-testid={`checkin-file-input-${i}`}
                              />
                              <button
                                onClick={() => onPickPhoto(i)}
                                disabled={uploadingIdx === i}
                                className="label-mono h-8 px-2 border border-current hover:bg-white hover:text-black hover-flip"
                                data-testid={`checkin-add-photo-${i}`}
                              >
                                {uploadingIdx === i ? "UPLOADING…" : "📷 ADD PHOTO"}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Challenge days info */}
        {challenge?.challenge_days_this_month?.length > 0 && (
          <div className="border border-[var(--stakes-border)] p-4">
            <div className="label-mono" style={{ color: "var(--stakes-admin)" }}>
              THIS MONTH&apos;S CHALLENGE DAYS
            </div>
            <div className="font-anton text-2xl uppercase mt-1" data-testid="challenge-days-list">
              {challenge.challenge_days_this_month.join(" · ")}
            </div>
            <div className="label-mono mt-1">
              RANDOM 2-DAY SPRINT IN THE LAST WEEK OF THE MONTH
            </div>
          </div>
        )}
      </main>

      {/* RIGHT COL — Global Intel (lg+ only). Renders Leaderboard above the
          Proof Wall + Ledger as one continuous vertical stack that scrolls
          with the rest of the dashboard. Below lg this is hidden entirely
          and the mobileTab swap takes over (see sections below). */}
      <aside
        className="hidden lg:flex lg:flex-col lg:gap-6 lg:min-w-0"
        data-testid="desktop-right-col"
      >
        <Leaderboard leagueId={league?.id} />
        <PastWinners leagueId={league?.id} leagueCode={league?.code} />
        <BragBoardPanel leagueId={league?.id} user={user} />
        <LeagueFeed leagueId={league?.id} />
        <ActivityLog leagueId={league?.id} />
      </aside>
      </div>

      {/* ============ Mobile-only alternate tab views (Phase 19) ============ */}
      {mobileTab === "leaderboard" && (
        <section
          className="lg:hidden max-w-[1600px] mx-auto px-4 py-4 space-y-4"
          data-testid="mobile-leaderboard-view"
        >
          <Leaderboard leagueId={league?.id} />
          <PastWinners leagueId={league?.id} leagueCode={league?.code} />
          <BragBoardPanel leagueId={league?.id} user={user} />
        </section>
      )}

      {mobileTab === "feed" && (
        <section
          className="lg:hidden max-w-[1600px] mx-auto px-4 py-4 space-y-4"
          data-testid="mobile-feed-view"
        >
          <LeagueFeed leagueId={league?.id} />
          <ActivityLog leagueId={league?.id} />
        </section>
      )}
      </div>

      {/* Mobile-only bottom nav. Desktop keeps the rich multi-column layout. */}
      <BottomNav active={mobileTab} onChange={setMobileTab} />
    </div>
  );
}

function PushBell({ userId }) {
  const [perm, setPerm] = useState(() => (typeof window !== "undefined" && "Notification" in window) ? Notification.permission : "unsupported");
  const supported = isPushSupported();
  if (!supported) return null;

  const onClick = async () => {
    const res = await enablePushForUser(userId);
    if (res.ok) {
      setPerm("granted");
      toast.success("Notifications armed");
    } else if (res.reason === "denied") {
      setPerm("denied");
      toast.error("Notifications blocked in browser settings");
    } else if (res.reason === "server_not_configured") {
      // Backend returned 503 — VAPID env vars missing on the deployed
      // backend. Show the actual detail so the operator knows exactly
      // what to set.
      toast.error(res.detail || "Server missing VAPID keys — set VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY on the backend and redeploy.", { duration: 10000 });
    } else if (res.reason === "no_key" || res.reason === "key_fetch_failed") {
      toast.error(`Push setup failed — ${res.detail || "VAPID public key unavailable from server"}`, { duration: 8000 });
    } else if (res.reason === "unsupported") {
      toast.error("This browser doesn't support push notifications");
    } else if (res.reason === "sw_failed") {
      toast.error("Service worker registration failed — try reloading the page");
    } else {
      toast.error(`Notification setup failed (${res.reason || "unknown"})`);
    }
  };

  const isOn = perm === "granted";
  return (
    <button
      onClick={onClick}
      disabled={perm === "denied"}
      className={`order-2 sm:order-3 label-mono px-2 sm:px-3 h-9 border flex-shrink-0 hover-flip ${isOn ? "border-[var(--neon-blue)] !text-[var(--neon-blue)]" : "border-[var(--stakes-border)] hover:border-white hover:!text-white"} disabled:opacity-30 disabled:cursor-not-allowed`}
      data-testid="dashboard-push-button"
      title={isOn ? "Notifications armed" : perm === "denied" ? "Notifications blocked — enable from browser settings" : "Arm push notifications"}
    >
      {isOn ? "🔔 ON" : perm === "denied" ? "🔕 BLOCKED" : "🔔 ARM"}
    </button>
  );
}
